<script setup>
import { computed } from '#imports';

const props = defineProps({
  author: {
    type: Object,
    required: true,
  },
});

const assetId = computed(() => props.author.image.asset._ref);
</script>

<template>
  <NuxtLink
    :to="{
      name: 'members-slug',
      params: { slug: author.slug.current },
    }"
    class="author-card"
  >
    <div class="author-card__avatar">
      <SanityImage
        class="author-card__avatar-image"
        :asset-id="assetId"
        :alt="author.name"
        :h="180"
        :w="180"
        fit="crop"
        dpr="3"
        auto="format"
      />
    </div>
    <div class="author-card__name">
      {{ author.name }}
    </div>
  </NuxtLink>
</template>

<style lang="postcss" scoped>
.author-card {
  --path-1: 'M170 80C170 124.183 134.183 160 90 160C45.8172 160 0 104.183 0 60C0 15.8172 45.8172 0 90 0C134.183 0 170 35.8172 170 80Z';
  --path-2: 'M160 80C160 124.183 94.1828 160 50 160C5.81722 160 0 124.183 0 80C0 35.8172 5.81722 0 50 0C94.1828 0 160 35.8172 160 80Z';
  --path-3: 'M160 80C160 124.183 144.183 160 100 160C55.8172 160 0 114.183 0 70C0 25.8172 35.8172 0 80 0C124.183 0 160 35.8172 160 80Z';
  --path-4: 'M160 70C160 114.183 134.183 150 90 150C45.8172 150 0 114.183 0 70C0 25.8172 55.8172 0 100 0C144.183 0 160 25.8172 160 70Z';
  --path-5: 'M170 100C170 144.183 124.183 160 80 160C35.8172 160 0 144.183 0 100C0 55.8172 35.8172 0 80 0C124.183 0 170 55.8172 170 100Z';
  --path-6: 'M180 80C180 124.183 154.183 160 110 160C65.8172 160 0 124.183 0 80C0 35.8172 25.8172 0 70 0C114.183 0 180 35.8172 180 80Z';
  --path-7: 'M160 100C160 144.183 104.183 160 60 160C15.8172 160 0 104.183 0 60C0 15.8172 65.8172 0 110 0C154.183 0 160 55.8172 160 100Z';
  --path-8: 'M150 100C122 133 94.1828 150 50 150C5.81722 150 0 94.1828 0 50C0 5.81722 85.8172 0 130 0C174.183 0 178 67 150 100Z';

  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  padding: 32px 0 0;
  border: 1px solid rgba(17, 17, 17, 10%);
  border-radius: 16px;
  height: 324px;
  min-width: 0;
  background-color: rgba(var(--white-color--rgb), 0.7);
  transition: 0.2s;

  &__avatar {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    height: 160px;
  }

  &__avatar-image {
    height: 160px;
    width: 160px;
    object-fit: cover;
    object-position: center;
  }

  &__name {
    padding: 0 16px;
    font-family: var(--title-font-family);
    font-size: 22px;
    line-height: 30px;
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    width: min-content;
  }

  &:hover {
    background-color: rgba(var(--white-color--rgb));
    color: var(--accent-color);
  }

  &:nth-child(1),
  &:nth-child(8n + 9) {
    .author-card__avatar-image {
      height: 160px;
      width: 170px;
      clip-path: path(var(--path-1));
    }
  }

  &:nth-child(2),
  &:nth-child(8n + 10) {
    .author-card__avatar-image {
      height: 160px;
      width: 160px;
      clip-path: path(var(--path-2));
    }
  }

  &:nth-child(3),
  &:nth-child(8n + 11) {
    .author-card__avatar-image {
      height: 160px;
      width: 160px;
      clip-path: path(var(--path-3));
    }
  }

  &:nth-child(4),
  &:nth-child(8n + 12) {
    .author-card__avatar-image {
      height: 150px;
      width: 160px;
      clip-path: path(var(--path-4));
    }
  }

  &:nth-child(5),
  &:nth-child(8n + 13) {
    .author-card__avatar-image {
      height: 160px;
      width: 170px;
      clip-path: path(var(--path-5));
    }
  }

  &:nth-child(6),
  &:nth-child(8n + 14) {
    .author-card__avatar-image {
      height: 160px;
      width: 180px;
      clip-path: path(var(--path-6));
    }
  }

  &:nth-child(7),
  &:nth-child(8n + 15) {
    .author-card__avatar-image {
      height: 160px;
      width: 160px;
      clip-path: path(var(--path-7));
    }
  }

  &:nth-child(8),
  &:nth-child(8n + 16) {
    .author-card__avatar-image {
      height: 150px;
      width: 168px;
      clip-path: path(var(--path-8));
    }
  }

  @media (--small-vp) {
    --path-1: 'M116 55C116 85.3757 91.56 110 61.4118 110C31.2635 110 0 71.6257 0 41.25C0 10.8743 31.2635 0 61.4118 0C91.56 0 116 24.6243 116 55Z';
    --path-2: 'M116 58C116 90.0325 68.2825 116 36.25 116C4.21748 116 0 90.0325 0 58C0 25.9675 4.21748 0 36.25 0C68.2825 0 116 25.9675 116 58Z';
    --path-3: 'M116 58C116 90.0325 104.533 116 72.5 116C40.4675 116 0 82.7825 0 50.75C0 18.7175 25.9675 0 58 0C90.0325 0 116 25.9675 116 58Z';
    --path-4: 'M116 50.75C116 82.7825 97.2825 108.75 65.25 108.75C33.2175 108.75 0 82.7825 0 50.75C0 18.7175 40.4675 0 72.5 0C104.533 0 116 18.7175 116 50.75Z';
    --path-5: 'M116 68.2353C116 98.3835 84.7365 109.176 54.5882 109.176C24.44 109.176 0 98.3835 0 68.2353C0 38.087 24.44 0 54.5882 0C84.7365 0 116 38.087 116 68.2353Z';
    --path-6: 'M116 51.5558C116 80.0292 99.3622 103.111 70.8889 103.111C42.4155 103.111 0 80.0292 0 51.5558C0 23.0825 16.6378 0.000244141 45.1111 0.000244141C73.5845 0.000244141 116 23.0825 116 51.5558Z';
    --path-7: 'M116 72.5002C116 104.533 75.5325 116 43.5 116C11.4675 116 0 75.5328 0 43.5002C0 11.4677 47.7175 0.000244141 79.75 0.000244141C111.783 0.000244141 116 40.4677 116 72.5002Z';
    --path-8: 'M103.689 69.126C84.3334 91.9375 65.1045 103.689 34.5629 103.689C4.02119 103.689 0 65.1048 0 34.5631C0 4.02144 59.3218 0.000244141 89.8634 0.000244141C120.405 0.000244141 123.044 46.3145 103.689 69.126Z';

    flex-direction: row;
    gap: 0;
    justify-content: flex-start;
    height: 142px;
    padding: 0 16px;

    &__avatar {
      height: auto;
    }

    &__name {
      text-align: left;
      max-width: min-content;
      font-size: 20px;
      line-height: 26px;
    }

    &:nth-child(1),
    &:nth-child(8n + 9) {
      .author-card__avatar-image {
        height: 110px;
        width: 116px;
      }
    }

    &:nth-child(2),
    &:nth-child(8n + 10) {
      .author-card__avatar-image {
        height: 116px;
        width: 116px;
      }
    }

    &:nth-child(3),
    &:nth-child(8n + 11) {
      .author-card__avatar-image {
        height: 116px;
        width: 116px;
      }
    }

    &:nth-child(4),
    &:nth-child(8n + 12) {
      .author-card__avatar-image {
        height: 109px;
        width: 116px;
      }
    }

    &:nth-child(5),
    &:nth-child(8n + 13) {
      .author-card__avatar-image {
        height: 110px;
        width: 116px;
      }
    }

    &:nth-child(6),
    &:nth-child(8n + 14) {
      .author-card__avatar-image {
        height: 104px;
        width: 116px;
      }
    }

    &:nth-child(7),
    &:nth-child(8n + 15) {
      .author-card__avatar-image {
        height: 116px;
        width: 116px;
      }
    }

    &:nth-child(8),
    &:nth-child(8n + 16) {
      .author-card__avatar-image {
        height: 104px;
        width: 116px;
      }
    }
  }
}
</style>
